import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getUser, removeAuthToken } from '@/utils/auth';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const user = getUser();

  const handleLogout = () => {
    removeAuthToken();
    navigate('/');
    window.location.reload();
  };

  return (
    <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo - Vertically Centered */}
          <Link to="/" className="flex items-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_bespoke-realty/artifacts/dg49n26m_BESPOKE%287%29.png"
              alt="Bespoke Realty"
              className="h-14 w-auto"
            />
          </Link>

          {/* Desktop Navigation - Updated text color to #434345 */}
          <div className="hidden md:flex items-center space-x-1">
            <Link to="/buy" className="px-4 py-2 text-sm font-medium text-[#434345] hover:text-[#BCA480] transition-colors">
              Buy
            </Link>
            <div className="relative group" data-testid="sell-menu">
              <Link to="/sell" className="px-4 py-2 text-sm font-medium text-[#434345] hover:text-[#BCA480] transition-colors">
                Sell
              </Link>
              <div className="absolute left-0 top-full pt-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition z-50">
                <div className="bg-white border border-gray-200 rounded-lg shadow-lg py-2 min-w-[220px]">
                  <Link to="/sell" className="block px-4 py-2 text-sm text-gray-700 hover:bg-[#faf8f4] hover:text-[#BCA480]" data-testid="nav-sell-home">Sell Your Home</Link>
                  <Link to="/home-valuation" className="block px-4 py-2 text-sm text-gray-700 hover:bg-[#faf8f4] hover:text-[#BCA480]" data-testid="nav-home-valuation">
                    <span className="font-semibold">What's My Home Worth? →</span>
                    <div className="text-[11px] text-gray-500">Free personalized valuation</div>
                  </Link>
                </div>
              </div>
            </div>
            <Link to="/new-construction" className="px-4 py-2 text-sm font-medium text-[#434345] hover:text-[#BCA480] transition-colors">
              New Construction
            </Link>
            <div className="relative group px-4 py-2" data-testid="property-management-menu">
              <button className="text-sm font-medium text-[#434345] hover:text-[#BCA480] transition-colors flex items-center gap-1">
                Property Management
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/></svg>
              </button>
              <div className="absolute left-0 top-full pt-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition z-50">
                <div className="bg-white border border-gray-200 rounded-lg shadow-lg py-2 min-w-[280px]">
                  <Link to="/property-management" className="block px-4 py-3 text-sm text-gray-700 hover:bg-[#faf8f4] hover:text-[#BCA480]" data-testid="nav-pm-rental">
                    <div className="font-semibold">Rental Property Management</div>
                    <div className="text-[11px] text-gray-500 mt-0.5">Single-family rentals &amp; investor portfolios</div>
                  </Link>
                  <Link to="/hoa-management" className="block px-4 py-3 text-sm text-gray-700 hover:bg-[#faf8f4] hover:text-[#BCA480]" data-testid="nav-pm-hoa">
                    <div className="font-semibold">HOA &amp; Association Management</div>
                    <div className="text-[11px] text-gray-500 mt-0.5">HOAs, condo associations, POAs</div>
                  </Link>
                  <div className="border-t border-gray-100 my-1"></div>
                  <Link to="/hoa/login" className="block px-4 py-3 text-sm text-gray-700 hover:bg-[#faf8f4] hover:text-[#BCA480]" data-testid="nav-pm-homeowner-portal">
                    <div className="font-semibold">Homeowner Portal Sign-In →</div>
                    <div className="text-[11px] text-gray-500 mt-0.5">View your dues balance &amp; ledger</div>
                  </Link>
                  <Link to="/title/login" className="block px-4 py-3 text-sm text-gray-700 hover:bg-[#faf8f4] hover:text-[#BCA480]" data-testid="nav-pm-title-portal">
                    <div className="font-semibold">Title Company Portal →</div>
                    <div className="text-[11px] text-gray-500 mt-0.5">Request HOA payoff / estoppel letters</div>
                  </Link>
                </div>
              </div>
            </div>
            <Link to="/agents" className="px-4 py-2 text-sm font-medium text-[#434345] hover:text-[#BCA480] transition-colors">
              Our Agents
            </Link>
            <Link to="/careers" className="px-4 py-2 text-sm font-medium text-[#434345] hover:text-[#BCA480] transition-colors" data-testid="nav-careers">
              Careers
            </Link>
            <Link to="/education" className="px-4 py-2 text-sm font-medium text-[#434345] hover:text-[#BCA480] transition-colors">
              Education
            </Link>
            <div className="relative group px-4 py-2" data-testid="explore-local-menu">
              <button className="text-sm font-medium text-[#BCA480] hover:text-[#a89470] transition-colors flex items-center gap-1">
                Explore Local
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/></svg>
              </button>
              <div className="absolute left-0 top-full pt-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition z-50">
                <div className="bg-white border border-gray-200 rounded-lg shadow-lg py-2 min-w-[260px]">
                  <div className="px-4 py-1.5 text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Tennessee</div>
                  <Link to="/memphis-tn-real-estate" className="block px-4 py-2 text-sm text-gray-700 hover:bg-[#faf8f4] hover:text-[#BCA480]" data-testid="nav-memphis">Memphis</Link>
                  <Link to="/collierville-tn-real-estate" className="block px-4 py-2 text-sm text-gray-700 hover:bg-[#faf8f4] hover:text-[#BCA480]" data-testid="nav-collierville">Collierville</Link>
                  <Link to="/germantown-tn-real-estate" className="block px-4 py-2 text-sm text-gray-700 hover:bg-[#faf8f4] hover:text-[#BCA480]" data-testid="nav-germantown">Germantown</Link>
                  <div className="px-4 py-1.5 text-[10px] uppercase tracking-wider text-gray-400 font-semibold mt-1 border-t border-gray-100">Mississippi</div>
                  <Link to="/southaven-ms-real-estate" className="block px-4 py-2 text-sm text-gray-700 hover:bg-[#faf8f4] hover:text-[#BCA480]" data-testid="nav-southaven">Southaven</Link>
                  <Link to="/olive-branch-ms-real-estate" className="block px-4 py-2 text-sm text-gray-700 hover:bg-[#faf8f4] hover:text-[#BCA480]" data-testid="nav-olive-branch">Olive Branch</Link>
                  <Link to="/hernando-ms-real-estate" className="block px-4 py-2 text-sm text-gray-700 hover:bg-[#faf8f4] hover:text-[#BCA480]" data-testid="nav-hernando">Hernando</Link>
                  <div className="border-t border-gray-100 mt-1">
                    <Link to="/guide" className="block px-4 py-2 text-sm text-[#BCA480] font-medium hover:bg-[#faf8f4]" data-testid="nav-guide">Full Local Guide →</Link>
                  </div>
                </div>
              </div>
            </div>
            <Link to="/blog" className="px-4 py-2 text-sm font-medium text-[#434345] hover:text-[#BCA480] transition-colors">
              Blog
            </Link>
            <Link to="/about" className="px-4 py-2 text-sm font-medium text-[#434345] hover:text-[#BCA480] transition-colors">
              About
            </Link>
            <Link to="/contact" className="px-4 py-2 text-sm font-medium text-[#434345] hover:text-[#BCA480] transition-colors">
              Contact
            </Link>
            
            {user ? (
              <>
                {(user.role === 'agent' || user.role === 'admin') && (
                  <>
                    <Link to="/agent-portal" className="ml-2">
                      <Button 
                        size="sm"
                        className="bg-[#BCA480] hover:bg-[#a89470] text-white"
                        data-testid="agent-portal-btn"
                      >
                        Agent Portal
                      </Button>
                    </Link>
                  </>
                )}
                {user.role === 'admin' && (
                  <Link to="/admin" className="ml-2">
                    <Button 
                      size="sm"
                      className="bg-[#434345] hover:bg-[#BCA480] text-white"
                      data-testid="admin-dashboard-btn"
                    >
                      Admin Dashboard
                    </Button>
                  </Link>
                )}
                <Button 
                  onClick={handleLogout}
                  variant="outline"
                  size="sm"
                  className="ml-2"
                  data-testid="logout-btn"
                >
                  Logout
                </Button>
              </>
            ) : (
              <div className="flex gap-2 ml-2">
                <Link to="/agent-portal">
                  <Button 
                    size="sm"
                    className="bg-[#BCA480] hover:bg-[#a89470] text-white shadow-md"
                    data-testid="agent-login-btn"
                  >
                    Agent Login
                  </Button>
                </Link>
                <Link to="/admin">
                  <Button 
                    size="sm"
                    className="bg-[#434345] hover:bg-[#BCA480] text-white"
                    data-testid="admin-login-btn"
                  >
                    Admin Login
                  </Button>
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 rounded-md text-gray-700 hover:text-[#BCA480] hover:bg-gray-100"
            data-testid="mobile-menu-btn"
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <div className="md:hidden border-t border-gray-200" data-testid="mobile-menu">
          <div className="px-2 pt-2 pb-3 space-y-1">
            <Link to="/buy" className="block px-3 py-2 text-base font-medium text-[#434345] hover:text-[#BCA480] hover:bg-gray-50" onClick={() => setIsOpen(false)}>
              Buy
            </Link>
            <Link to="/sell" className="block px-3 py-2 text-base font-medium text-[#434345] hover:text-[#BCA480] hover:bg-gray-50" onClick={() => setIsOpen(false)}>
              Sell
            </Link>
            <Link to="/new-construction" className="block px-3 py-2 text-base font-medium text-[#434345] hover:text-[#BCA480] hover:bg-gray-50" onClick={() => setIsOpen(false)}>
              New Construction
            </Link>
            <Link to="/property-management" className="block px-3 py-2 text-base font-medium text-[#434345] hover:text-[#BCA480] hover:bg-gray-50" onClick={() => setIsOpen(false)}>
              Property Management
            </Link>
            <Link to="/hoa-management" className="block px-3 py-2 pl-6 text-sm font-medium text-[#434345] hover:text-[#BCA480] hover:bg-gray-50" onClick={() => setIsOpen(false)}>
              — HOA &amp; Association Management
            </Link>
            <Link to="/hoa/login" className="block px-3 py-2 pl-6 text-sm font-medium text-[#434345] hover:text-[#BCA480] hover:bg-gray-50" onClick={() => setIsOpen(false)}>
              — Homeowner Portal Sign-In →
            </Link>
            <Link to="/agents" className="block px-3 py-2 text-base font-medium text-[#434345] hover:text-[#BCA480] hover:bg-gray-50" onClick={() => setIsOpen(false)}>
              Our Agents
            </Link>
            <Link to="/careers" className="block px-3 py-2 text-base font-medium text-[#434345] hover:text-[#BCA480] hover:bg-gray-50" onClick={() => setIsOpen(false)} data-testid="nav-careers-mobile">
              Careers
            </Link>
            <Link to="/education" className="block px-3 py-2 text-base font-medium text-[#434345] hover:text-[#BCA480] hover:bg-gray-50" onClick={() => setIsOpen(false)}>
              Education
            </Link>
            <Link to="/blog" className="block px-3 py-2 text-base font-medium text-[#434345] hover:text-[#BCA480] hover:bg-gray-50" onClick={() => setIsOpen(false)}>
              Blog
            </Link>
            <Link to="/about" className="block px-3 py-2 text-base font-medium text-[#434345] hover:text-[#BCA480] hover:bg-gray-50" onClick={() => setIsOpen(false)}>
              About
            </Link>
            <Link to="/contact" className="block px-3 py-2 text-base font-medium text-[#434345] hover:text-[#BCA480] hover:bg-gray-50" onClick={() => setIsOpen(false)}>
              Contact
            </Link>
            {user && (
              <>
                {(user.role === 'agent' || user.role === 'admin') && (
                  <Link to="/agent-portal" className="block px-3 py-2 text-base font-medium text-[#BCA480] font-semibold hover:bg-gray-50" onClick={() => setIsOpen(false)}>
                    Agent Portal
                  </Link>
                )}
                {user.role === 'admin' && (
                  <Link to="/admin" className="block px-3 py-2 text-base font-medium text-[#434345] hover:text-[#BCA480] hover:bg-gray-50" onClick={() => setIsOpen(false)}>
                    Admin Dashboard
                  </Link>
                )}
                <button 
                  onClick={() => { handleLogout(); setIsOpen(false); }}
                  className="w-full text-left px-3 py-2 text-base font-medium text-[#434345] hover:text-[#BCA480] hover:bg-gray-50"
                >
                  Logout
                </button>
              </>
            )}
            {!user && (
              <>
                <Link to="/agent-portal" className="block px-3 py-2 text-base font-medium text-[#BCA480] font-semibold hover:bg-gray-50" onClick={() => setIsOpen(false)}>
                  Agent Login
                </Link>
                <Link to="/admin" className="block px-3 py-2 text-base font-medium text-[#434345] hover:text-[#BCA480] hover:bg-gray-50" onClick={() => setIsOpen(false)}>
                  Admin Login
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
